import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import type { AgentTask } from "@forgepoint/shared";

/** URL param that deep-links the open task panel of a thread view. */
export const TASK_ID_PARAM = "taskId";

interface UseTaskDeepLinkOptions {
  enabled: boolean;
  selectedTaskId: string | null;
}

/**
 * Mirrors a thread view's open task panel into the `taskId` URL param and resolves an incoming one.
 * @param options.enabled - True when the host owns the URL; false leaves the URL untouched
 * @param options.selectedTaskId - The task whose panel is open, or null
 * @returns `openLinkedTask`, which opens the linked task once the thread's open tasks have loaded and returns true when a link was handled
 */
export const useTaskDeepLink = ({
  enabled,
  selectedTaskId,
}: UseTaskDeepLinkOptions) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedTaskId = enabled ? searchParams.get(TASK_ID_PARAM) : null;
  const prevSelectedTaskIdRef = useRef<string | null>(null);

  const writeTaskId = useCallback(
    (taskId: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (taskId) {
            next.set(TASK_ID_PARAM, taskId);
          } else {
            next.delete(TASK_ID_PARAM);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Acts only on selection transitions, so a pending link is never cleared
  // before the thread's tasks load, and a link written by someone else stays.
  useEffect(() => {
    const prevSelectedTaskId = prevSelectedTaskIdRef.current;
    prevSelectedTaskIdRef.current = selectedTaskId;
    if (!enabled || selectedTaskId === prevSelectedTaskId) {
      return;
    }
    if (selectedTaskId) {
      if (linkedTaskId !== selectedTaskId) {
        writeTaskId(selectedTaskId);
      }
      return;
    }
    if (linkedTaskId === prevSelectedTaskId) {
      writeTaskId(null);
    }
  }, [enabled, selectedTaskId, linkedTaskId, writeTaskId]);

  const openLinkedTask = useCallback(
    (openTasks: AgentTask[], openTaskPanel: (taskId: string) => void) => {
      if (!linkedTaskId) {
        return false;
      }
      if (openTasks.some((task) => task.id === linkedTaskId)) {
        openTaskPanel(linkedTaskId);
        return true;
      }
      toast("This task was already resolved");
      writeTaskId(null);
      return true;
    },
    [linkedTaskId, writeTaskId],
  );

  return { openLinkedTask };
};
